import React from 'react'
import PropTypes from 'prop-types'
import {isEditing} from '@builder.io/sdk-react'
import {conditionOptionsMap} from 'hec/components/cta/toggle-text-button/utils'
import {coreButtonInputs, coreButtonPropTypes} from './cta.helper'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import {ToggleTextButton, buttonFunctions} from 'hec/components/cta'
import useFetchPageDataForCta from './useFetchPageDataForCta'
import HeroToggleTextButton from './HeroToggleTextButton'

const BuilderToggleTextButton = ({
    condition,
    conditionMetText,
    isLearningCenterHero,
    heroRole,
    conditionNotMetText,
    stateToShowInEditor,
    disabled,
    variant,
    openInNewTab,
    clickFunction = buttonFunctions.linkToPage,
    scrollTargetId,
    isInternalLink,
    isRevShare,
    pageReference,
    queryParams,
    externalLinkReference,
    attributes,
    'data-testid': dataTestId
}) => {
    const {className: attrClassName, ...restAttributes} = attributes || {}
    const isEdit = isEditing()
    const isHero = isLearningCenterHero === true || isLearningCenterHero === 'true'

    const {
        pageRef,
        externalLinkRef,
        isLoading: isPageDataLoading,
        error: pageDataError
    } = useFetchPageDataForCta({
        isInternalLink,
        pageReference,
        externalLinkReference
    })

    const {routeType, url} = useDetermineRouteTypeAndUrl({
        clickFunction,
        isInternalLink,
        pageReference: pageRef,
        externalLinkReference: externalLinkRef
    })

    if (isPageDataLoading) {
        return null
    }

    if (pageDataError) {
        console.error('Error fetching page data for CTA: ', pageDataError)
        return (
            <ToggleTextButton
                ctaType="button"
                condition={condition}
                conditionMetText={conditionMetText}
                conditionNotMetText={conditionNotMetText}
                stateToShowInEditor={stateToShowInEditor}
                disabled={true}
                variant={variant}
                isEdit={isEdit}
                className={attrClassName}
                {...restAttributes}
                data-testid={dataTestId || 'toggleTextButton-ToggleTextButton-error'}
            />
        )
    }

    const HeroToggleTextBtnProps = {
        condition,
        conditionMetText,
        conditionNotMetText,
        stateToShowInEditor,
        clickFunction,
        attributes,
        heroRole,
        dataTestId,
        scrollTargetId,
        isRevShare
    }

    return isHero ? (
        <HeroToggleTextButton {...HeroToggleTextBtnProps} />
    ) : (
        <ToggleTextButton
            ctaType="button"
            condition={condition}
            conditionMetText={conditionMetText}
            conditionNotMetText={conditionNotMetText}
            stateToShowInEditor={stateToShowInEditor}
            disabled={disabled}
            variant={variant}
            openInNewTab={openInNewTab}
            clickFunction={clickFunction}
            scrollTargetId={scrollTargetId}
            isRevShare={isRevShare}
            queryParams={queryParams}
            routeType={routeType}
            url={url}
            isEdit={isEdit}
            className={attrClassName}
            {...restAttributes}
            data-testid={dataTestId || 'toggleTextButton-ToggleTextButton'}
        />
    )
}
BuilderToggleTextButton.propTypes = {
    condition: PropTypes.string.isRequired,
    conditionMetText: PropTypes.string.isRequired,
    conditionNotMetText: PropTypes.string.isRequired,
    stateToShowInEditor: PropTypes.string.isRequired,
    isLearningCenterHero: PropTypes.string,
    heroRole: PropTypes.oneOf(['take_quiz', 'continue']),
    ...coreButtonPropTypes
}
export const ToggleTextButtonDefinition = {
    component: BuilderToggleTextButton,
    name: 'ToggleTextButton',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/link.svg',
    noWrap: true,
    inputs: [
        {
            name: 'condition',
            type: 'string',
            enum: Object.values(conditionOptionsMap),
            required: true
        },
        {
            name: 'conditionMetText',
            type: 'string',
            required: true
        },
        {
            name: 'conditionNotMetText',
            type: 'string',
            required: true
        },
        {
            name: 'stateToShowInEditor',
            type: 'string',
            enum: ['Condition Met', 'Condition Not Met'],
            defaultValue: 'Condition Met'
        },
        {
            name: 'variant',
            type: 'string',
            enum: ['primary', 'secondary'],
            defaultValue: 'primary'
        },
        {
            name: 'isLearningCenterHero',
            type: 'string',
            enum: ['true', 'false'],
            defaultValue: 'false'
        },
        {
            name: 'heroRole',
            type: 'string',
            enum: ['take_quiz', 'continue'],
            helperText:
                'Declare which hero action this instance represents so the component can auto-highlight the correct CTA and wire Continue to last-visited.',
            defaultValue: 'take_quiz'
        },
        {
            name: 'disabled',
            type: 'boolean',
            defaultValue: false
        },
        ...coreButtonInputs
    ]
}
export default BuilderToggleTextButton
